import { type ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { tenantApi } from '../../api/endpoints/tenant';
import { applyBrandingToDocument, clearBrandingFromDocument } from '../../lib/theme';
import { resolvePlatformContainerTenantId } from '../../lib/brandingTenant';

export function BrandingProvider({ children }: { children: ReactNode }) {
    // El platform admin tiene tenantId '*' (scopeRef global); su branding es el de la ficha
    // [platform] → mapeamos '*' → 'platform'. Tenant normal pasa tal cual.
    const brandingTenantId = resolvePlatformContainerTenantId(
        useAuthStore((s) => s.user?.tenantId),
    );
    const branding = useBrandingStore((s) => s.branding);
    const isCustom = useBrandingStore((s) => s.isCustom);
    const applyTenant = useBrandingStore((s) => s.applyTenantBranding);
    const resetToDefaults = useBrandingStore((s) => s.resetToFiadoDefaults);

    const { data } = useQuery({
        queryKey: ['branding', brandingTenantId],
        queryFn: () => {
            if (!brandingTenantId) throw new Error('tenantId required');
            return tenantApi.getBranding(brandingTenantId);
        },
        enabled: !!brandingTenantId,
        staleTime: 10 * 60_000,
    });

    useEffect(() => {
        if (!brandingTenantId) {
            resetToDefaults();
            return;
        }
        if (data) applyTenant(data);
    }, [brandingTenantId, data, applyTenant, resetToDefaults]);

    useEffect(() => {
        if (isCustom) applyBrandingToDocument(branding);
        else clearBrandingFromDocument();
    }, [branding, isCustom]);

    return <>{children}</>;
}
